/**
 * Inventory Wizard Modal
 *
 * 3-step wizard for creating/editing inventories.
 * Step 1: Choose type and products
 * Step 2: Count products (with auto-save)
 * Step 3: Review and validate
 *
 * @see US-12.7 - Modale Wizard 3 etapes
 * @see US-12.8 - Composants saisie avec auto-save
 */

import { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check, AlertTriangle } from 'lucide-react';
import { Button, Modal, Skeleton, Loader } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { Calendar } from 'lucide-react';
import {
  useInventory,
  useCreateInventory,
  useValidateInventory,
  useInventoryPreview,
} from '../hooks/useInventories';
import { WizardStep1 } from './wizard/WizardStep1';
import { WizardStep2 } from './wizard/WizardStep2';
import { WizardStep3 } from './wizard/WizardStep3';
import type { CreateInventoryDto } from '@bstock/shared';
import { InventoryStatus } from '@bstock/shared';

interface InventoryWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  inventoryId?: string | null;
  onSuccess?: () => void;
}

type WizardStep = 1 | 2 | 3;

const STEP_LABELS = ['Configuration', 'Comptage', 'Validation'];

export function InventoryWizardModal({
  isOpen,
  onClose,
  inventoryId,
  onSuccess,
}: InventoryWizardModalProps) {
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();

  // State
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [createdInventoryId, setCreatedInventoryId] = useState<string | null>(null);
  const [isValidationConfirmed, setIsValidationConfirmed] = useState(false);

  // The actual inventory ID to work with (passed or just created)
  const activeInventoryId = inventoryId || createdInventoryId;

  // Fetch existing inventory if editing
  const { data: inventory, isLoading: inventoryLoading, refetch } = useInventory(
    isOpen && activeInventoryId ? activeInventoryId : null
  );

  // Fetch preview for step 3
  const { data: preview } = useInventoryPreview(
    currentStep === 3 && activeInventoryId ? activeInventoryId : null
  );

  // Mutations
  const createMutation = useCreateInventory();
  const validateMutation = useValidateInventory();

  // Determine initial step based on inventory status
  useEffect(() => {
    if (inventory) {
      if (inventory.status === InventoryStatus.DRAFT && inventory.summary.countedLines === 0) {
        setCurrentStep(2); // Go to counting step
      } else if (inventory.status === InventoryStatus.IN_PROGRESS) {
        setCurrentStep(2); // Continue counting
      }
    } else if (!inventoryId) {
      setCurrentStep(1); // New inventory starts at step 1
    }
  }, [inventory, inventoryId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setCreatedInventoryId(null);
      setIsValidationConfirmed(false);
    }
  }, [isOpen]);

  // Reset confirmation when leaving step 3
  useEffect(() => {
    if (currentStep !== 3) {
      setIsValidationConfirmed(false);
    }
  }, [currentStep]);

  // Handle step 1 completion (create inventory)
  const handleCreateInventory = useCallback(async (data: CreateInventoryDto) => {
    const result = await createMutation.mutateAsync(data);
    setCreatedInventoryId(result.id);
    setCurrentStep(2);
  }, [createMutation]);

  // Handle step navigation
  const handleNextStep = useCallback(() => {
    if (currentStep < 3) {
      setCurrentStep((prev) => (prev + 1) as WizardStep);
      // No need to manually refetch - React Query will do it when the query becomes enabled
    }
  }, [currentStep]);

  const handlePrevStep = useCallback(() => {
    if (currentStep > 1 && (currentStep > 2 || !activeInventoryId)) {
      setCurrentStep((prev) => (prev - 1) as WizardStep);
    }
  }, [currentStep, activeInventoryId]);

  // Handle validation
  const handleValidate = useCallback(async () => {
    if (!activeInventoryId) return;

    await validateMutation.mutateAsync(activeInventoryId);
    onSuccess?.();
    onClose();
  }, [activeInventoryId, validateMutation, onSuccess, onClose]);

  // Handle close with confirmation if in progress
  const handleClose = useCallback(() => {
    if (currentStep === 2 && inventory && inventory.summary.countedLines > 0) {
      // Auto-save happens automatically, just close
      onClose();
    } else {
      onClose();
    }
  }, [currentStep, inventory, onClose]);

  // Calculate progress
  const progress = inventory?.summary.progressPercent ?? 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={inventoryId ? 'Inventaire' : 'Nouvel inventaire'}
      description={inventory?.inventoryNumber || 'Configuration de l\'inventaire'}
      size="5xl"
      className="max-h-[90vh]"
    >
      <div className="flex flex-col h-[75vh] relative">
        {/* Validation loading overlay */}
        {validateMutation.isPending && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-lg">
            <Loader size="lg" />
            <p className="mt-4 text-sm font-medium text-neutral-700">
              Validation en cours...
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              Generation des mouvements de stock
            </p>
          </div>
        )}

        {/* Step indicator */}
        <div className="pb-4 border-b border-neutral-200 flex-shrink-0 -mt-2">
          <div className="flex items-center w-full">
            {[1, 2, 3].map((step) => (
              <div key={step} className="contents">
                {/* Step circle and label */}
                <div className="flex items-center flex-shrink-0">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      step === currentStep
                        ? 'bg-primary-500 text-white'
                        : step < currentStep
                        ? 'bg-success-100 text-success-700'
                        : 'bg-neutral-100 text-neutral-500'
                    }`}
                  >
                    {step < currentStep ? <Check className="w-4 h-4" /> : step}
                  </div>
                  <span className={`ml-2 text-sm whitespace-nowrap ${
                    step === currentStep ? 'text-dark-500 font-medium' : 'text-neutral-500'
                  }`}>
                    {STEP_LABELS[step - 1]}
                  </span>
                </div>
                {/* Connector line - takes remaining space */}
                {step < 3 && (
                  <div className={`flex-1 h-0.5 mx-4 min-w-[20px] ${
                    step < currentStep ? 'bg-success-400' : 'bg-neutral-200'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Progress bar for step 2 */}
          {currentStep === 2 && inventory && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-neutral-500 mb-2">
                <span>Progression du comptage</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Effective date warning */}
        {inventory?.effectiveDate && (
          <div className="mx-6 mt-4 flex items-center gap-2 rounded-lg bg-warning-50 border border-warning-200 px-4 py-3 text-sm text-warning-700">
            <Calendar className="h-4 w-4 flex-shrink-0" />
            <span>
              Date d'effet definie : les mouvements seront enregistres au{' '}
              <strong>{formatDateShort(inventory.effectiveDate)}</strong>
            </span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {inventoryLoading && activeInventoryId ? (
            <div className="space-y-4">
              <Skeleton className="h-12" />
              <Skeleton className="h-64" />
            </div>
          ) : (
            <>
              {currentStep === 1 && (
                <WizardStep1
                  onSubmit={handleCreateInventory}
                  isLoading={createMutation.isPending}
                />
              )}

              {currentStep === 2 && inventory && (
                <WizardStep2
                  inventory={inventory}
                  onRefresh={refetch}
                />
              )}

              {currentStep === 3 && inventory && preview && (
                <WizardStep3
                  inventory={inventory}
                  preview={preview}
                  isConfirmed={isValidationConfirmed}
                  onConfirmationChange={setIsValidationConfirmed}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 pt-4 border-t border-neutral-200 flex-shrink-0">
          <div>
            {currentStep === 3 && preview && preview.summary.linesWithVariance > 0 && (
              <div className="flex items-center gap-2 text-sm text-warning-600">
                <AlertTriangle className="w-4 h-4" />
                <span>
                  {preview.summary.linesWithVariance} ecart(s) -
                  Valeur nette: {formatCentimes(preview.summary.netVarianceValue, currency)}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {currentStep > 1 && currentStep < 3 && (
              <Button
                variant="secondary"
                onClick={handlePrevStep}
                disabled={activeInventoryId !== null && currentStep === 2}
              >
                <ChevronLeft className="w-4 h-4" />
                Retour
              </Button>
            )}

            {currentStep === 2 && (
              <Button onClick={handleNextStep}>
                Verifier
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}

            {currentStep === 3 && (
              <>
                <Button variant="secondary" onClick={handlePrevStep}>
                  <ChevronLeft className="w-4 h-4" />
                  Modifier
                </Button>
                <Button
                  onClick={handleValidate}
                  disabled={!isValidationConfirmed}
                  isLoading={validateMutation.isPending}
                >
                  <Check className="w-4 h-4" />
                  Valider l'inventaire
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
